/**
 * /orders/kitchen — chef-facing orders page.
 *
 * Fully on the REAL backend: `useKitchenShipmentsQuery` loads the
 * kitchen's shipments for the visible range and
 * `shipmentsToCampuses` projects them into the chefs-v2 `Campus`
 * tree the cards/panels/print packet render. Each deliverable row is
 * a real menu item (Component) — its recipe (ingredients + steps),
 * dietary flags, and cover image ride the shipment wire, so the
 * detail panel and the print Ingredient List + Recipe pages render
 * real content (no empty states). Service days derive from shipment
 * presence (a campus with no shipment on a date is "off" that day).
 *
 * Substitution flow is end-to-end real: the modal binds to
 * `useSubstitutionRequestsQuery` + `useCreateSubstitutionRequestMutation`
 * against the `/api/substitution-requests` REST surface.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { Dialog, DialogContent, DialogTitle } from '@ordo/ui/dialog'
import { EmptyState } from '@ordo/ui/empty-state'
import { toast } from '@ordo/ui/sonner'
import { usePrintAction } from '@ordo/ui/use-print-action'
import { ArrowLeftRight, Calendar, ChefHat, Eye, Printer } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router'

import {
  isEveryServedSchoolNoServiceOnDate,
  useKitchenShipmentsQuery,
  useMyKitchensQuery,
  useNextOrderDateQuery,
} from '@/api/kitchen-orders'
import { CHIP_CLASS, CHIP_CLASS_PRIMARY, ChipButton } from '@/components/chefs/chip-button'
import { DateRangeStepper } from '@/components/chefs/date-range-stepper'
import { DeliverableDetailPanel } from '@/components/chefs/deliverable-detail-panel'
import { MissedDeadlineSummaryBanner } from '@/components/chefs/missed-deadline-summary-banner'
import { MobileActionPill } from '@/components/chefs/mobile-action-pill'
import { NoServiceEmptyState } from '@/components/chefs/no-service-empty-state'
import { OrderSummaryCard } from '@/components/chefs/order-summary-card'
import { PacketMeasureHost, type PacketPlan } from '@/components/chefs/print/packet/packet-measure-host'
import { PrintDialog } from '@/components/chefs/print/print-dialog'
import { PrintPreliminaryWatermark } from '@/components/chefs/print/print-preliminary-watermark'
import { SubstitutionModal } from '@/components/chefs/substitution-modal'
import { SubstitutionResolutionBanner } from '@/components/chefs/substitution-resolution-banner'
import { TempLogGate } from '@/components/chefs/temp-log-gate'
import { OrderDeadlineAlert } from '@/components/orders/school/order-deadline-alert'
import { WeekMonthCalendar } from '@/components/orders/school/week-month-calendar'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import { ActiveKitchenProvider, useActiveKitchen } from '@/hooks/chefs/useActiveKitchen'
import { useCampusOrderStates } from '@/hooks/chefs/useCampusOrderState'
import { useChefDateRange } from '@/hooks/chefs/useChefDateRange'
import { useIsNarrow } from '@/hooks/chefs/useIsNarrow'
import { useIsScrolled } from '@/hooks/chefs/useIsScrolled'
import { useKitchenPacketPrint } from '@/hooks/chefs/useKitchenPacketPrint'
import { MeasurementPreferenceProvider, useMeasurementPreference } from '@/hooks/chefs/useMeasurementPreference'
import { useOnPassed } from '@/hooks/chefs/useOnPassed'
import { usePrintDeliveryRoute } from '@/hooks/chefs/usePrintDeliveryRoute'
import { useSubstitutionModal } from '@/hooks/chefs/useSubstitutionModal'
import { formatShortDay } from '@/lib/chefs/format'
import { buildChefOrderCalendar } from '@/lib/chefs/order-calendar'
import { packetModelFor } from '@/lib/chefs/packet/model'
import type { PacketSections, PacketTarget } from '@/lib/chefs/packet/target'
import {
  formatPrintLock,
  nextPrintLockAt,
  printLocksAt,
  printPreliminary,
  wantsPrintOnLoad,
  withoutPrintOnLoad,
} from '@/lib/chefs/print-lock'
import type { PrintDay } from '@/lib/chefs/print-plan'
import {
  buildServiceDays,
  dailyEmptyDayLabel,
  formatRangeForCopy,
  isoDate,
  isPredictedRange,
  localDateFromKey,
  predictedCutoff,
  rangeServiceWeekKeys,
  rangeWindow,
  reanchorRange,
  shouldShowProvisionalBanner,
  startOfWorkWeek,
} from '@/lib/chefs/service-days'
import { serviceStatusFromShipments, shipmentsForDay, shipmentsToCampuses } from '@/lib/chefs/shipment-to-campus'
import { computeTotalMeals } from '@/lib/chefs/totals'
import type { Deliverable } from '@/lib/chefs/types'
import { weekOf } from '@/lib/school-orders/calendar'
import { findDeliverableById, resolveKitchenGate, sortCampusesForOrders } from '@/pages/orders/kitchen-helpers'
import { KitchenOrdersSkeleton } from '@/skeletons/kitchen-orders-skeleton'
import { useChefSidebarCollapse } from '@/stores/chefs/sidebar'

// Default kitchen label shown on printed packets when the chef's
// kitchen org name hasn't loaded yet.
const FALLBACK_KITCHEN_NAME = 'My Kitchen'

export function KitchenOrdersPage({ switcher }: { switcher?: React.ReactNode }) {
  const kitchensQuery = useMyKitchensQuery()
  const activeKitchen = kitchensQuery.data?.me?.kitchenLocations?.[0] ?? null
  const kitchenLocationId = activeKitchen?.id ?? null
  const kitchenName = activeKitchen?.name ?? FALLBACK_KITCHEN_NAME
  // Resolved-to-zero-kitchens viewers (mis-provisioned chefs, ORDO_ADMINs)
  // get an explicit membership empty state instead of the silent empty
  // "My Kitchen" schedule this page used to degrade into.
  const kitchensEmpty =
    resolveKitchenGate({
      pending: kitchensQuery.isPending,
      errored: kitchensQuery.isError,
      kitchenCount: kitchensQuery.data?.me?.kitchenLocations?.length ?? 0,
    }) === 'EMPTY'

  return (
    <ActiveKitchenProvider kitchenLocationId={kitchenLocationId}>
      <MeasurementPreferenceProvider kitchenOrganizationId={activeKitchen?.organizationId ?? null}>
        <SubstitutionResolutionBanner />
        <OrdersBody
          kitchenName={kitchenName}
          kitchensPending={kitchensQuery.isPending}
          kitchensEmpty={kitchensEmpty}
          switcher={switcher}
        />
        <SubstitutionModal />
      </MeasurementPreferenceProvider>
    </ActiveKitchenProvider>
  )
}

function OrdersBody({
  kitchenName,
  kitchensPending,
  kitchensEmpty,
  switcher,
}: {
  kitchenName: string
  kitchensPending: boolean
  kitchensEmpty: boolean
  switcher?: React.ReactNode
}) {
  const initialSelectedId = (() => {
    if (typeof window === 'undefined') {
      return null
    }
    return new URLSearchParams(window.location.search).get('select')
  })()
  const [selectedDeliverableId, setSelectedDeliverableId] = useState<string | null>(initialSelectedId)
  // Chefs default to the Daily view anchored on today. The range is shared with
  // /shopping-list and mirrored into `?date=&view=` so flipping between the two
  // surfaces (or reloading) holds the chef's place — see `useChefDateRange`.
  const { range, today, setRange } = useChefDateRange('daily')
  const { anchor, granularity } = range
  const [printDialogOpen, setPrintDialogOpen] = useState(false)
  // The moment a printout's "are these counts final?" is judged (PROD-1452).
  // A state value, never `new Date()` inline, so the selection the dialog
  // measures and the one Print prints always agree. Bumped when the dialog
  // opens and when a lock below passes.
  const [printClock, setPrintClock] = useState(() => new Date())
  const openPrintDialog = useCallback(() => {
    setPrintClock(new Date())
    setPrintDialogOpen(true)
  }, [])
  const [fullscreenDeliverable, setFullscreenDeliverable] = useState<Deliverable | null>(null)
  // Live target reflecting the dialog's current section/day selection, measured
  // off-screen so the dialog can preview an accurate page count.
  const [previewTarget, setPreviewTarget] = useState<PacketTarget | null>(null)
  // The dialog's raw selection, for the reads the packet needs (the route).
  const [printSelection, setPrintSelection] = useState<{ sections: PacketSections; dates: Set<string> } | null>(null)
  // Latest measured plan: the packet laid out on fixed pages from rendered heights.
  const [measuredPlan, setMeasuredPlan] = useState<PacketPlan | null>(null)
  // The recipe pages follow the kitchen's Standard | Technical setting.
  const { mode: measurement } = useMeasurementPreference()

  const setSelectedDeliverable = (d: Deliverable | null) => {
    setSelectedDeliverableId(d?.id ?? null)
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && selectedDeliverableId) {
        setSelectedDeliverableId(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectedDeliverableId])

  // Real shipments for the visible range; the adapter projects them
  // into the `Campus` tree everything below renders.
  const kitchenLocationId = useActiveKitchen()
  const { startDate, endDate } = useMemo(() => {
    const { start, end } = rangeWindow(range)
    return { startDate: isoDate(start), endDate: isoDate(end) }
  }, [range])
  const shipmentsQuery = useKitchenShipmentsQuery(
    { kitchenLocationId: kitchenLocationId ?? '', startDate, endDate },
    { enabled: Boolean(kitchenLocationId) },
  )
  const shipments = useMemo(() => shipmentsQuery.data?.shipments ?? [], [shipmentsQuery.data])
  // PROD-1503 — `?print=1` (the link in the chef "orders ready" email and SMS)
  // opens Print Orders once the range's orders have loaded, then drops the
  // param so a reload or a shared link does not reopen the dialog.
  const [searchParams, setSearchParams] = useSearchParams()
  const printRequested = wantsPrintOnLoad(searchParams)
  const printParamHandled = useRef(false)
  useEffect(() => {
    if (!printRequested || printParamHandled.current || !shipmentsQuery.isSuccess) {
      return
    }
    printParamHandled.current = true
    openPrintDialog()
    setSearchParams((prev) => withoutPrintOnLoad(prev), { replace: true })
  }, [printRequested, shipmentsQuery.isSuccess, openPrintDialog, setSearchParams])
  const campuses = useMemo(() => sortCampusesForOrders(shipmentsToCampuses(shipments)), [shipments])
  // PROD-176 — a chef serving a single location gets their order details
  // auto-expanded (nothing to scan past); multi-location chefs keep the
  // collapsed scan view.
  const autoExpand = campuses.length <= 1

  // PROD-178 — "Go to next day with orders" in the empty state. Only fetched
  // while the empty state is actually visible (range settled with no orders).
  // `after` = the visible range's end, so the jump is strictly forward.
  const nextOrderDateQuery = useNextOrderDateQuery(
    { kitchenLocationId: kitchenLocationId ?? '', after: endDate },
    {
      enabled: Boolean(kitchenLocationId) && shipmentsQuery.isSuccess && campuses.length === 0,
    },
  )
  const nextOrderDate = nextOrderDateQuery.data?.nextDate ?? null
  const goToNextOrderDate = useCallback(() => {
    if (!nextOrderDate) {
      return
    }
    // Daily → land on that day; weekly → the range derives its week from the
    // anchor, so the same anchor set jumps to that date's week; custom → the
    // window keeps its length and slides to start there.
    setRange(reanchorRange(range, localDateFromKey(nextOrderDate.slice(0, 10))))
  }, [nextOrderDate, range, setRange])
  const nextOrderAction = nextOrderDate
    ? { label: 'Go to next day with orders', onClick: goToNextOrderDate }
    : undefined

  // Right-rail month calendar: a separate month-scoped shipments read (padded a
  // week each side so a week spanning a month boundary is fully covered) feeds
  // the per-school "Orders confirmed" / "Orders incoming" coloring + countdown.
  const { monthStart, monthEnd } = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1 - 7)
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0 + 7)
    return { monthStart: isoDate(first), monthEnd: isoDate(last) }
  }, [anchor])
  const monthShipmentsQuery = useKitchenShipmentsQuery(
    { kitchenLocationId: kitchenLocationId ?? '', startDate: monthStart, endDate: monthEnd },
    { enabled: Boolean(kitchenLocationId) },
  )
  const orderCal = useMemo(
    () =>
      buildChefOrderCalendar(
        monthShipmentsQuery.data?.shipments ?? [],
        today,
        monthShipmentsQuery.data?.servedSchoolDeadlines ?? [],
      ),
    [monthShipmentsQuery.data, today],
  )
  const anchorMondayKey = useMemo(() => isoDate(startOfWorkWeek(anchor)), [anchor])
  const weekKeys = useMemo(() => weekOf(anchorMondayKey), [anchorMondayKey])
  const dayKey = useMemo(() => isoDate(anchor), [anchor])
  const isDaily = granularity === 'daily'
  const isCustom = granularity === 'custom'
  const representativePolicy = orderCal.representativePolicy(anchorMondayKey)

  // A campus with no shipment on a date is "off" that day.
  const serviceDays = useMemo(
    () => buildServiceDays(range, serviceStatusFromShipments(campuses, shipments)),
    [range, campuses, shipments],
  )
  // The custom window's own days, for the month calendar's selection band.
  // Weekends are dropped: the grid renders weekdays only, so a weekend key at
  // either end would put the band's rounded cap on a cell that never draws.
  const rangeDayKeys = useMemo(
    () => serviceDays.filter((d) => d.date.getDay() !== 0 && d.date.getDay() !== 6).map((d) => isoDate(d.date)),
    [serviceDays],
  )
  const isPredicted = useMemo(() => isPredictedRange(range, today), [range, today])
  // We always show the real placed orders (no preview/estimate). A compact
  // "may still change" note shows next to the calendar only while real orders
  // exist and the ordering cutoff is still open.
  const showProvisional = useMemo(
    () => shouldShowProvisionalBanner(range, today, shipments.length > 0),
    [range, today, shipments.length],
  )
  const provisionalCutoff = useMemo(() => predictedCutoff(range), [range])
  // PROD-1452 — when the visible range's orders lock. A print started from the
  // browser menu can't be swapped for the packet (see usePrintAction below), so
  // while this is set that print carries the watermark. Re-judged when it passes.
  const rangeLocksAt = useMemo(
    () =>
      printLocksAt({
        dayKeys: serviceDays.map((day) => isoDate(day.date)),
        shipments,
        servedSchools: shipmentsQuery.data?.servedSchoolDeadlines ?? [],
        noServiceDays: shipmentsQuery.data?.servedSchoolNoServiceDays ?? [],
        now: printClock,
      }),
    [serviceDays, shipments, shipmentsQuery.data, printClock],
  )
  useOnPassed(rangeLocksAt, () => setPrintClock(new Date()))
  const offDaysByCampus = useMemo(() => {
    const map = new Map<string, typeof serviceDays>()
    for (const day of serviceDays) {
      for (const c of day.offCampuses) {
        const list = map.get(c.id) ?? []
        list.push(day)
        map.set(c.id, list)
      }
    }
    return map
  }, [serviceDays])
  // Per-day campus arrays for the print packet — each day rebuilt from
  // that day's own shipments, so per-day quantities are exact.
  const campusesByDay = useMemo<PrintDay[]>(() => {
    return serviceDays.map((day) => {
      const iso = isoDate(day.date)
      return {
        iso,
        date: day.date,
        dateLabel: formatShortDay(day.date),
        campuses: sortCampusesForOrders(shipmentsToCampuses(shipmentsForDay(shipments, iso))),
      }
    })
  }, [shipments, serviceDays])

  // The delivery route, read while the print dialog has a selection: the packet
  // letters its stops in route order and prints each stop's maps code.
  const printRouteDayKeys = useMemo(
    () =>
      printSelection
        ? campusesByDay.filter((d) => printSelection.dates.has(d.iso) && d.campuses.length > 0).map((d) => d.iso)
        : [],
    [printSelection, campusesByDay],
  )
  const printRoute = usePrintDeliveryRoute({
    kitchenLocationId,
    wanted: printSelection !== null,
    dayKeys: printRouteDayKeys,
  })

  const buildPrintTarget = useCallback(
    (sections: PacketSections, selectedDates: Set<string>): PacketTarget | null => {
      const days: PrintDay[] = campusesByDay.filter((d) => selectedDates.has(d.iso) && d.campuses.length > 0)
      if (days.length === 0) {
        return null
      }
      // Not ready until the route lands (the read starts the render after the
      // dialog opens): measuring or printing without it would letter the stops
      // out of route order.
      if (!printRoute.asked || printRoute.pending) {
        return null
      }

      // PROD-1452 — a school on these days can still change its order: the
      // printout is PRELIMINARY, and the pages that school's counts reach carry
      // the INCOMPLETE ORDER watermark.
      const preliminary = printPreliminary({
        dayKeys: days.map((d) => d.iso),
        shipments,
        servedSchools: shipmentsQuery.data?.servedSchoolDeadlines ?? [],
        noServiceDays: shipmentsQuery.data?.servedSchoolNoServiceDays ?? [],
        now: printClock,
      })

      return {
        sections,
        days,
        kitchenName,
        route: printRoute.route ?? null,
        measurement,
        // The route's one code opens the route page on this app (the Oct 20 all-hands).
        routeLinkOrigin: window.location.origin,
        ...(preliminary ? { preliminary } : {}),
      }
    },
    [
      campusesByDay,
      kitchenName,
      shipments,
      shipmentsQuery.data,
      printClock,
      printRoute.route,
      printRoute.pending,
      printRoute.asked,
      measurement,
    ],
  )

  // The dialog reports its current selection here so we can measure it.
  const handleSelectionChange = useCallback(
    (sections: PacketSections, selectedDates: Set<string>) => {
      setPrintSelection({ sections, dates: selectedDates })
      setPreviewTarget(buildPrintTarget(sections, selectedDates))
    },
    [buildPrintTarget],
  )

  // The packet prints as a finished PDF wherever the browser can print one in
  // place (useKitchenPacketPrint explains why): it is prepared while the
  // dialog is open, and Print runs synchronously inside the tap.
  const packet = useKitchenPacketPrint({
    kitchenLocationId,
    dialogOpen: printDialogOpen,
    selection: previewTarget,
    measured: measuredPlan,
    scopeKey: `${kitchenLocationId ?? ''}|${startDate}|${endDate}`,
  })

  function triggerPrint(sections: PacketSections, selectedDates: Set<string>): boolean {
    const target = buildPrintTarget(sections, selectedDates)
    return target ? packet.print(target) : false
  }

  // Cmd/Ctrl+P opens the Print Orders dialog instead of printing the raw
  // screen. A print started from the browser menu / mobile share sheet can't
  // be redirected (the packet needs async measurement), so after one runs we
  // open the real dialog and say why the printout wasn't the packet.
  usePrintAction({
    onPrintRequest: openPrintDialog,
    printing: packet.printing,
    onUnhandledSystemPrint: () => {
      openPrintDialog()
      toast.info('To print the orders packet, use Print Orders — pick the days and sections here.')
    },
  })

  // Measure the dialog's live selection: its page count and the packet's
  // contents numbers.
  const measureTarget = previewTarget
  // A print dialog left open across a school's lock prints that school's pages final.
  const selectionLocksAt = useMemo(
    () =>
      previewTarget?.preliminary
        ? nextPrintLockAt({
            dayKeys: previewTarget.days.map((day) => day.iso),
            shipments,
            servedSchools: shipmentsQuery.data?.servedSchoolDeadlines ?? [],
            noServiceDays: shipmentsQuery.data?.servedSchoolNoServiceDays ?? [],
            now: printClock,
          })
        : null,
    [previewTarget, shipments, shipmentsQuery.data, printClock],
  )
  useOnPassed(printDialogOpen ? selectionLocksAt : null, () => setPrintClock(new Date()))

  const previewPlan = useMemo(() => {
    if (!previewTarget || !measuredPlan) {
      return null
    }
    return measuredPlan.signature === packetModelFor(previewTarget).signature
      ? { totalPages: measuredPlan.totalPages, dayCount: measuredPlan.dayCount }
      : null
  }, [previewTarget, measuredPlan])

  const orderStates = useCampusOrderStates(campuses, today, isPredicted)
  const missedDeadlineCount = useMemo(() => {
    let n = 0
    for (const state of orderStates.values()) {
      if (state.status === 'missed') {
        n += 1
      }
    }
    return n
  }, [orderStates])
  const noServiceInRange = serviceDays.length > 0 && serviceDays.every((d) => d.status === 'none')
  // An order window is still open → an empty view means "no orders yet"
  // (schools can still order), not a genuine off day. Asked per DAY rather than
  // off the anchor's week: a custom window can straddle a week boundary, and
  // ordering is still incoming while ANY week it touches is open.
  const ordersIncoming = useMemo(
    () => rangeServiceWeekKeys(range).some((monday) => !orderCal.isWeekClosed(monday)),
    [orderCal, range],
  )
  // A genuine off day (weekend) is the only case that should read "No service";
  // a served weekday that closed with zero orders reads "No orders". Weekday
  // off-days per kitchen (holidays / non-serving weekdays) aren't plumbed into
  // this shipment-derived view, so only weekends are treated as off days here.
  const selectedDayIsOffDay = useMemo(() => {
    if (range.granularity !== 'daily') {
      return false
    }
    const day = range.anchor.getDay()
    return day === 0 || day === 6
  }, [range])
  const selectedDayIsNoServiceDay = useMemo(() => {
    if (range.granularity !== 'daily') {
      return false
    }
    return isEveryServedSchoolNoServiceOnDate(
      shipmentsQuery.data?.servedSchoolDeadlines ?? [],
      shipmentsQuery.data?.servedSchoolNoServiceDays ?? [],
      dayKey,
    )
  }, [dayKey, range.granularity, shipmentsQuery.data])
  // Daily empty-state label: weekday alone within a direct week, weekday + date
  // ("Tuesday 21") further out so it's clear which day.
  const dailyDayLabel = useMemo(() => dailyEmptyDayLabel(range, today), [range, today])

  // Range-wide production total across ALL schools — the number Ops
  // previously summed school-by-school (Yanping Ren). True meal count
  // (never re-derived from merged rows; see computeMealtimeMeals).
  const totalMeals = useMemo(() => computeTotalMeals(campusesByDay.flatMap((day) => day.campuses)), [campusesByDay])

  const selectedDeliverable = useMemo<Deliverable | null>(
    () => (selectedDeliverableId ? findDeliverableById(campuses, selectedDeliverableId) : null),
    [selectedDeliverableId, campuses],
  )

  const [renderedDeliverable, setRenderedDeliverable] = useState<Deliverable | null>(null)
  if (selectedDeliverable && selectedDeliverable !== renderedDeliverable) {
    setRenderedDeliverable(selectedDeliverable)
  }
  useEffect(() => {
    if (selectedDeliverable || !renderedDeliverable) {
      return
    }
    const t = window.setTimeout(() => setRenderedDeliverable(null), 200)
    return () => window.clearTimeout(t)
  }, [selectedDeliverable, renderedDeliverable])

  const openSubstitutionLedger = useSubstitutionModal((s) => s.openModal)
  const slotOpen = Boolean(selectedDeliverable)
  const isNarrow = useIsNarrow()
  const scrollRef = useRef<HTMLDivElement>(null)
  const isScrolled = useIsScrolled(scrollRef)

  // Collapse the global sidebar while the deliverable detail panel is open
  // so the panel has room (mirrors chefs-v2's standalone-shell behavior).
  const setChefSidebarCollapsed = useChefSidebarCollapse((s) => s.setCollapsed)
  useEffect(() => {
    setChefSidebarCollapsed(slotOpen)
    return () => setChefSidebarCollapsed(false)
  }, [slotOpen, setChefSidebarCollapsed])

  if (kitchensPending || (Boolean(kitchenLocationId) && shipmentsQuery.isPending)) {
    return <KitchenOrdersSkeleton />
  }

  // Zero kitchens resolved (settled, no error): keep the header + switcher
  // (multi-hat users must not lose the variant switch) and say WHY the
  // schedule is empty rather than rendering a blank week for "My Kitchen".
  if (kitchensEmpty) {
    return (
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={Calendar} title="Orders" className="mb-0" />
          {switcher}
        </div>
        <div className="px-4 pt-6 lg:px-0">
          <EmptyState
            icon={<ChefHat />}
            title="No kitchen memberships"
            description="Kitchen orders show up here once you're added as an admin or staff to a kitchen organization."
          />
        </div>
      </div>
    )
  }

  if (shipmentsQuery.error) {
    return (
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={Calendar} title="Orders" className="mb-0" />
          {switcher}
        </div>
        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-destructive">Couldn&apos;t load this range&apos;s orders.</p>
            <p className="mt-1 text-xs text-muted-foreground">{shipmentsQuery.error.message}</p>
          </div>
          <ChipButton className={CHIP_CLASS} onClick={() => void shipmentsQuery.refetch()}>
            Retry
          </ChipButton>
        </div>
      </div>
    )
  }

  // Temp-log gate: the server withheld this range's orders because the chef
  // has overdue temperature logs. Render the unlock path instead of the
  // calendar — no toolbar, no meals pill, no date stepper (every range is
  // equally gated).
  const tempLogGate = shipmentsQuery.data?.tempLogGate
  if (tempLogGate && tempLogGate.overdueDays.length > 0) {
    return (
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={Calendar} title="Orders" className="mb-0" />
          {switcher}
        </div>
        <TempLogGate days={tempLogGate.overdueDays} />
      </div>
    )
  }

  return (
    <>
      <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-0 lg:px-4">
        <div className="px-4 pt-4 lg:px-0 lg:pt-5">
          <OrdoPageHeader icon={Calendar} title="Orders" className="mb-0" />
          {switcher}
        </div>
        {missedDeadlineCount > 0 && !isPredicted ? (
          <div className="pt-4 lg:pt-5">
            <MissedDeadlineSummaryBanner count={missedDeadlineCount} />
          </div>
        ) : null}
        <div
          className={cn(
            'relative z-10 bg-background transition-[box-shadow] duration-200 ease-out',
            isScrolled && 'shadow-[0_6px_8px_-6px_rgba(0,0,0,0.12)]',
          )}>
          <div className="flex items-center justify-between gap-3 px-2 pb-3 pt-4 lg:py-5 lg:pl-0 lg:pr-3">
            <DateRangeStepper range={range} today={today} onRangeChange={setRange} />
            <div className="flex items-center gap-2">
              {totalMeals > 0 ? (
                // Production total across every school in the visible range —
                // quiet, non-interactive sibling of the action chips.
                <span
                  title={`Ordered meals across all schools ${formatRangeForCopy(range, today)}`}
                  className="inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-full border border-border bg-muted/40 px-3 text-sm text-muted-foreground">
                  <span className="font-semibold tabular-nums text-foreground">{totalMeals.toLocaleString()}</span>
                  meals
                </span>
              ) : null}
              <ChipButton
                Icon={ArrowLeftRight}
                className={cn(CHIP_CLASS, 'hidden lg:inline-flex')}
                onClick={() => openSubstitutionLedger({ view: 'form' })}>
                Substitute an Ingredient
              </ChipButton>
              <ChipButton
                variant="default"
                Icon={Printer}
                className={cn(CHIP_CLASS_PRIMARY, 'hidden lg:inline-flex')}
                onClick={openPrintDialog}>
                Print Orders
              </ChipButton>
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 items-stretch gap-2">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:pr-1">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
              <div
                ref={scrollRef}
                data-scroll-container
                className="min-h-0 flex-1 overflow-y-auto pb-24 lg:pb-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                {campuses.length === 0 ? (
                  selectedDayIsNoServiceDay ? (
                    <NoServiceEmptyState noServiceDay weekday={dailyDayLabel} action={nextOrderAction} />
                  ) : ordersIncoming ? (
                    <NoServiceEmptyState ordersIncoming weekday={dailyDayLabel} action={nextOrderAction} />
                  ) : noServiceInRange ? (
                    <NoServiceEmptyState
                      weekday={dailyDayLabel}
                      isOffDay={selectedDayIsOffDay}
                      action={nextOrderAction}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
                      <p className="text-lg font-medium">No orders for this period.</p>
                      <p className="text-sm text-muted-foreground">
                        Try clearing filters or selecting a different date range.
                      </p>
                      {nextOrderAction ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-1"
                          onClick={nextOrderAction.onClick}>
                          {nextOrderAction.label}
                        </Button>
                      ) : null}
                    </div>
                  )
                ) : (
                  <div className="flex flex-col gap-3 px-2 pb-4 lg:px-0">
                    {campuses.map((campus) => (
                      <OrderSummaryCard
                        // PROD-176 — `defaultOpen` only seeds useState, so the
                        // key carries the flag: when the single-location flip
                        // resolves after the query lands, the card remounts open.
                        key={`${campus.id}-${autoExpand}`}
                        campus={campus}
                        defaultOpen={autoExpand}
                        offDays={offDaysByCampus.get(campus.id) ?? []}
                        orderState={orderStates.get(campus.id)}
                        onSelectDeliverable={setSelectedDeliverable}
                        selectedDeliverableId={selectedDeliverableId ?? undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {!isNarrow &&
            (slotOpen || renderedDeliverable ? (
              // A deliverable is open → the side peek takes the right column
              // (the calendar is hidden while it's up).
              <div
                className={cn(
                  'relative shrink-0 overflow-hidden transition-[width] duration-200 ease-out',
                  slotOpen ? 'w-[clamp(440px,45vw,600px)]' : 'w-0',
                )}>
                {renderedDeliverable ? (
                  <div className="absolute inset-y-0 left-0 w-[clamp(440px,45vw,600px)] pb-4">
                    <DeliverableDetailPanel
                      deliverable={renderedDeliverable}
                      onClose={() => setSelectedDeliverable(null)}
                      onFullscreen={(d) => {
                        setFullscreenDeliverable(d)
                        setSelectedDeliverable(null)
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="hidden w-[240px] shrink-0 pb-4 lg:block lg:pl-1">
                <WeekMonthCalendar
                  anchorKey={isDaily || isCustom ? dayKey : anchorMondayKey}
                  // Daily → single-day selection (filled day, outlined today);
                  // Weekly → week band; Custom → a band over the window's own
                  // days (the grid hides weekends, so those simply don't draw).
                  selectedWeekKeys={isCustom ? rangeDayKeys : isDaily ? [] : weekKeys}
                  selectedDayKey={isDaily ? dayKey : undefined}
                  orderedDates={orderCal.orderedDates}
                  isWeekClosed={orderCal.isWeekClosed}
                  orderedLegendLabel="Orders confirmed"
                  openLegendLabel="Orders incoming"
                  {...(isDaily || isCustom
                    ? { onSelectDay: (key: string) => setRange(reanchorRange(range, localDateFromKey(key))) }
                    : {
                        onSelectWeek: (mondayKey: string) =>
                          setRange(reanchorRange(range, localDateFromKey(mondayKey))),
                      })}
                  footer={
                    representativePolicy ? (
                      <OrderDeadlineAlert compact policy={representativePolicy} weekMondayKey={anchorMondayKey} />
                    ) : undefined
                  }
                />
                {showProvisional ? (
                  <div className="mt-3 flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900">
                    <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                    <span>
                      Orders so far — schools can still change quantities until {formatShortDay(provisionalCutoff)}.
                    </span>
                  </div>
                ) : null}
              </div>
            ))}
        </div>
      </div>

      <MobileActionPill
        hidden={slotOpen}
        SecondaryIcon={ArrowLeftRight}
        secondaryLabel="Substitute"
        onSecondary={() => openSubstitutionLedger({ view: 'form' })}
        onPrintAll={openPrintDialog}
      />

      {isNarrow && selectedDeliverable ? (
        <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-background">
          <DeliverableDetailPanel deliverable={selectedDeliverable} onClose={() => setSelectedDeliverable(null)} />
        </div>
      ) : null}

      <PrintDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        onPrint={triggerPrint}
        onSelectionChange={handleSelectionChange}
        preview={previewPlan}
        serviceDays={serviceDays}
        showDaySelector={granularity !== 'daily'}
        status={packet.status}
        onRetry={packet.retry}
        preliminaryLocksAt={previewTarget?.preliminary?.locksAtLabel ?? null}
      />
      <Dialog open={!!fullscreenDeliverable} onOpenChange={(next) => (next ? null : setFullscreenDeliverable(null))}>
        <DialogContent hideCloseButton className="p-0 sm:max-w-3xl sm:rounded-2xl sm:h-[85vh]">
          {/* Radix requires an accessible name; the panel renders its own
              heading, so this is the screen-reader-only equivalent. */}
          <DialogTitle className="sr-only">{fullscreenDeliverable?.name ?? 'Deliverable'}</DialogTitle>
          {fullscreenDeliverable ? (
            <DeliverableDetailPanel
              variant="fullscreen"
              deliverable={fullscreenDeliverable}
              onClose={() => setFullscreenDeliverable(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      <PacketMeasureHost target={measureTarget} onMeasured={setMeasuredPlan} />
      {/* A browser-menu print of this page while orders are open. The app shell
          is print-screen-only, so without a print-only portal that print is a
          blank sheet; this one prints the INCOMPLETE ORDER watermark instead,
          and the dialog that opens after it (onUnhandledSystemPrint) prints
          the real packet. Dropped while a packet print owns the paper. */}
      {rangeLocksAt && shipments.length > 0 && !packet.printing
        ? createPortal(
            <div className="print-only">
              <PrintPreliminaryWatermark locksAtLabel={formatPrintLock(rangeLocksAt)} />
            </div>,
            document.body,
          )
        : null}
      {packet.hosts}
    </>
  )
}
